import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Typography,
  Chip,
  Stack,
  alpha,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AttachMoney as MoneyIcon,
  QrCode as BarcodeIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';
import { itemUnitsService, unitsService } from '../../services/items.service';
import { useConfirm } from '../../hooks/useConfirm';
import { ItemUnit, CreateItemUnitDto, Unit } from '../../types/items.types';

const translations = {
  en: {
    manageUnits: 'Manage Item Units',
    addUnit: 'Add Unit',
    unit: 'Unit *',
    conversionFactor: 'Conversion Factor *',
    barcode: 'Barcode',
    salePrice: 'Sale Price',
    purchasePrice: 'Purchase Price',
    defaultSale: 'Default Sale',
    defaultPurchase: 'Default Purchase',
    defaultBranch: 'Default Branch',
    notes: 'Notes',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    actions: 'Actions',
    noUnits: 'No units configured for this item',
    deleteConfirm: 'Delete this unit?',
    unitDetails: 'Unit Details',
    pricing: 'Pricing',
  },
  ar: {
    manageUnits: 'إدارة وحدات الصنف',
    addUnit: 'إضافة وحدة',
    unit: 'الوحدة *',
    conversionFactor: 'معامل التحويل *',
    barcode: 'الباركود',
    salePrice: 'سعر البيع',
    purchasePrice: 'سعر الشراء',
    defaultSale: 'افتراضي للبيع',
    defaultPurchase: 'افتراضي للشراء',
    defaultBranch: 'وحدة الفرع',
    notes: 'ملاحظات',
    save: 'حفظ',
    cancel: 'إلغاء',
    close: 'إغلاق',
    actions: 'الإجراءات',
    noUnits: 'لا توجد وحدات مكونة لهذا الصنف',
    deleteConfirm: 'حذف هذه الوحدة؟',
    unitDetails: 'تفاصيل الوحدة',
    pricing: 'التسعير',
  },
};

interface ItemUnitsDialogProps {
  open: boolean;
  onClose: () => void;
  itemId: number;
  itemName: string;
}

const fmtPrice = (v: number): string => {
  const parts = v.toFixed(7).split('.');
  const dec = parts[1].replace(/0+$/, '').padEnd(3, '0');
  return `${parts[0]}.${dec}`;
};

const ItemUnitsDialog: React.FC<ItemUnitsDialogProps> = ({ open, onClose, itemId, itemName }) => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const gridRef = useRef<HTMLDivElement>(null);
  useGridEnterNavigation(gridRef);

  const [itemUnits, setItemUnits] = useState<ItemUnit[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUnit, setEditingUnit] = useState<ItemUnit | null>(null);
  const [salePriceInput, setSalePriceInput] = useState('0');
  const [purchasePriceInput, setPurchasePriceInput] = useState('0');

  const [formData, setFormData] = useState<CreateItemUnitDto>({
    itemId: itemId,
    unitId: 0,
    conversionFactor: 1,
    barcode: '',
    defaultSalePrice: 0,
    defaultPurchasePrice: 0,
    isDefaultSale: false,
    isDefaultPurchase: false,
    isBranchUnit: false,
    notes: '',
  });

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, itemId]);

  const loadData = async () => {
    try {
      const [itemUnitsData, unitsData] = await Promise.all([
        itemUnitsService.getItemUnits(itemId),
        unitsService.getAll(),
      ]);
      setItemUnits(itemUnitsData);
      setUnits(unitsData);
    } catch (err) {
      console.error('Failed to load data', err);
    }
  };

  const handleAdd = () => {
    setEditingUnit(null);
    setSalePriceInput('0');
    setPurchasePriceInput('0');
    setFormData({
      itemId: itemId,
      unitId: 0,
      conversionFactor: 1,
      barcode: '',
      defaultSalePrice: 0,
      defaultPurchasePrice: 0,
      isDefaultSale: false,
      isDefaultPurchase: false,
      isBranchUnit: false,
      notes: '',
    });
    setShowForm(true);
  };

  const handleEdit = (itemUnit: ItemUnit) => {
    setEditingUnit(itemUnit);
    setSalePriceInput(String(itemUnit.defaultSalePrice));
    setPurchasePriceInput(String(itemUnit.defaultPurchasePrice));
    setFormData({
      itemId: itemId,
      unitId: itemUnit.unitId,
      conversionFactor: itemUnit.conversionFactor,
      barcode: itemUnit.barcode || '',
      defaultSalePrice: itemUnit.defaultSalePrice,
      defaultPurchasePrice: itemUnit.defaultPurchasePrice,
      isDefaultSale: itemUnit.isDefaultSale,
      isDefaultPurchase: itemUnit.isDefaultPurchase,
      isBranchUnit: itemUnit.isBranchUnit,
      notes: itemUnit.notes || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (itemUnitId: number) => {
    if (!(await confirm({ message: t.deleteConfirm, severity: 'error' }))) return;
    try {
      await itemUnitsService.delete(itemUnitId);
      await loadData();
    } catch (err) {
      console.error('Failed to delete', err);
      alert('Failed to delete unit');
    }
  };

  const handleSubmit = async () => {
    try {
      const salePrice = parseFloat(salePriceInput) || 0;
      const purchasePrice = parseFloat(purchasePriceInput) || 0;
      if (editingUnit) {
        const payload = {
          itemUnitId: editingUnit.itemUnitId,
          conversionFactor: formData.conversionFactor,
          barcode: formData.barcode,
          defaultSalePrice: salePrice,
          defaultPurchasePrice: purchasePrice,
          isDefaultSale: formData.isDefaultSale,
          isDefaultPurchase: formData.isDefaultPurchase,
          isBranchUnit: formData.isBranchUnit,
          notes: formData.notes,
        };
        await itemUnitsService.update(editingUnit.itemUnitId, payload);
      } else {
        await itemUnitsService.create({ ...formData, defaultSalePrice: salePrice, defaultPurchasePrice: purchasePrice });
      }
      await loadData();
      setShowForm(false);
    } catch (err) {
      console.error('Failed to save', err);
      alert('Failed to save unit');
    }
  };

  const selectedUnit = units.find(u => u.unitId === formData.unitId) || null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #2a2a2a 0%, #1e1e1e 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        },
      }}
    >
      <DialogTitle
        sx={{
          direction: isRTL ? 'rtl' : 'ltr',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
            {t.manageUnits}
          </Box>
          <Box sx={{ fontSize: '0.875rem', opacity: 0.9 }}>
            {itemName}
          </Box>
        </Stack>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ direction: isRTL ? 'rtl' : 'ltr', mt: 2 }}>
        {!showForm && (
          <>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAdd}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  },
                }}
              >
                {t.addUnit}
              </Button>
            </Box>

            <Box ref={gridRef}>
            <TableContainer
              component={Paper}
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background: alpha(theme.palette.primary.main, 0.1),
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.conversionFactor}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.barcode}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.salePrice}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.purchasePrice}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.defaultSale}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.defaultPurchase}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.defaultBranch}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">{t.actions}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemUnits.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t.noUnits}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    itemUnits.map((iu) => (
                        <TableRow
                          key={iu.itemUnitId}
                          hover
                          sx={{
                            '&:hover': {
                              background: alpha(theme.palette.primary.main, 0.05),
                            },
                          }}
                        >
                          <TableCell>
                            <Chip
                              label={isRTL ? iu.unitNameAr : iu.unitName}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label={`×${iu.conversionFactor}`} size="small" />
                          </TableCell>
                          <TableCell>
                            {iu.barcode ? (
                              <Chip
                                label={iu.barcode}
                                size="small"
                                icon={<BarcodeIcon />}
                                variant="outlined"
                              />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${fmtPrice(iu.defaultSalePrice)} KD`}
                              size="small"
                              icon={<MoneyIcon />}
                              color="success"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${fmtPrice(iu.defaultPurchasePrice)} KD`}
                              size="small"
                              icon={<MoneyIcon />}
                              color="info"
                            />
                          </TableCell>
                          <TableCell>
                            {iu.isDefaultSale ? (
                              <Chip icon={<CheckCircleIcon />} label="Yes" size="small" color="success" />
                            ) : (
                              <Chip icon={<CancelIcon />} label="No" size="small" color="default" />
                            )}
                          </TableCell>
                          <TableCell>
                            {iu.isDefaultPurchase ? (
                              <Chip icon={<CheckCircleIcon />} label="Yes" size="small" color="success" />
                            ) : (
                              <Chip icon={<CancelIcon />} label="No" size="small" color="default" />
                            )}
                          </TableCell>
                          <TableCell>
                            {iu.isBranchUnit ? (
                              <Chip icon={<StoreIcon />} label="Yes" size="small" color="warning" />
                            ) : (
                              <Chip icon={<CancelIcon />} label="No" size="small" color="default" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(iu)}
                                color="primary"
                                sx={{
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                    transition: 'transform 0.2s',
                                  },
                                }}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(iu.itemUnitId)}
                                color="error"
                                sx={{
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                    transition: 'transform 0.2s',
                                  },
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>
          </>
        )}

        {showForm && (
          <Box sx={{ mt: 2 }}>
            {/* Unit Details */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                {t.unitDetails}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                <Autocomplete
                  options={units}
                  getOptionLabel={(option) => isRTL ? option.unitNameAr : option.unitName}
                  value={selectedUnit}
                  onChange={(_, newValue) => setFormData({ ...formData, unitId: newValue?.unitId || 0 })}
                  renderInput={(params) => <TextField {...params} label={t.unit} required />}
                  disabled={!!editingUnit}
                />
                <TextField
                  label={t.conversionFactor}
                  value={formData.conversionFactor === 0 ? '' : String(formData.conversionFactor)}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || /^\d*\.?\d*$/.test(v)) {
                      setFormData({ ...formData, conversionFactor: v === '' || v === '.' ? 0 : parseFloat(v) });
                    }
                  }}
                  required
                  inputProps={{ inputMode: 'decimal' }}
                />
                <TextField
                  label={t.barcode}
                  value={formData.barcode}
                  onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                />
              </Box>
            </Box>

            {/* Pricing */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                {t.pricing}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <TextField
                  label={t.salePrice}
                  value={salePriceInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || v === '-' || /^-?\d*\.?\d*$/.test(v)) setSalePriceInput(v);
                  }}
                  inputProps={{ inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <Typography>KD</Typography>,
                  }}
                />
                <TextField
                  label={t.purchasePrice}
                  value={purchasePriceInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || v === '-' || /^-?\d*\.?\d*$/.test(v)) setPurchasePriceInput(v);
                  }}
                  inputProps={{ inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <Typography>KD</Typography>,
                  }}
                />
              </Box>
            </Box>

            {/* Defaults */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isDefaultSale}
                    onChange={(e) => setFormData({ ...formData, isDefaultSale: e.target.checked })}
                    color="success"
                  />
                }
                label={t.defaultSale}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isDefaultPurchase}
                    onChange={(e) => setFormData({ ...formData, isDefaultPurchase: e.target.checked })}
                    color="info"
                  />
                }
                label={t.defaultPurchase}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isBranchUnit}
                    onChange={(e) => setFormData({ ...formData, isBranchUnit: e.target.checked })}
                    color="warning"
                  />
                }
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <StoreIcon sx={{ fontSize: 18, color: formData.isBranchUnit ? '#ed6c02' : '#bbb' }} />
                    <span>{t.defaultBranch}</span>
                  </Stack>
                }
              />
            </Box>

            {/* Notes */}
            <TextField
              fullWidth
              label={t.notes}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
            />

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={() => setShowForm(false)} variant="outlined">
                {t.cancel}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  },
                }}
              >
                {t.save}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ direction: isRTL ? 'rtl' : 'ltr', p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {t.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemUnitsDialog;